<template>
  <!--
  ==========================================================================
  General
  ==========================================================================
  -->
  <form v-if="filteredContextList.length > 0" class="h-100 d-flex flex-column">
    <b-row no-gutters>
      <tag-alternatives
        :value="unsatisfiedTagAlternatives"
        class="bg-warning col p-2"
      />
    </b-row>
    <b-row no-gutters>
      <options-form-field
        ref="contextSelector"
        class="mb-0 col"
        dropdown
        :value="selectedContext"
        :options="getOptions()"
        :value2id="context2id"
        :id2value="id2context"
        @input="selectContext($event)"
      />
      <b-button
        v-if="selectedContext"
        variant="success"
        :disabled="!canUpload()"
        class="col-2"
        @click.prevent="$refs.attachments.showModal()"
      >
        <i class="fa fa-fw fa-upload"></i>
        <span class="d-sm-inline-block d-none ml-1">Upload</span>
      </b-button>
      <b-button
        v-if="selectedContext"
        variant="primary"
        class="col-2"
        @click.prevent="$refs.attachments.exportAttachments()"
      >
        <i class="fa fa-fw fa-download"></i>
        <span class="d-sm-inline-block d-none ml-1">{{
          $t("common.export")
        }}</span>
      </b-button>
    </b-row>
    <attachments
      v-if="selectedContext"
      ref="attachments"
      :context="resolveContextServerPath(selectedContext)"
      :context-id="selectedContext.getId(value)"
      :subcontext="selectedContext.subcontext"
      :permissions="selectedContext.getPermissions(value)"
      :unsatisfied-tag-alternatives="unsatisfiedTagAlternatives"
      class="flex-grow-1"
      >
      <b-alert
        variant="warning"
        show
        v-if="selectedContext && selectedContext.message"
        v-html="selectedContext.message"
        class="m-0"
      ></b-alert>
      <b-alert
        variant="danger"
        show
        v-if="selectedContext && !canUpload()"
        class="m-0"
      >
        <!-- TODO i18n -->
        Puoi scaricare allegati da questa sezione ma non sei autorizzato a
        caricarne di nuovi.
      </b-alert>
    </attachments>
  </form>
</template>
<script>
import EditorMixin from "vue-template/common/mixins/EditorMixin";
import Attachments from "./Attachments";
import TagAlternatives from "./TagAlternatives";
import notifier from "@/services/NotificationService";
import { beep } from "core/utils";

/** This must be binded to the editor */
export function onAttachmentEvent(evt) {
  console.log("ATTACHMENT-EVENT", evt);
  if (evt.type === "UPLOAD" && this.currentTab.id !== "attachments") {
    if (this.tabCounters.attachments == undefined) {
      this.tabCounters = {
        ...this.tabCounters,
        attachments: 1
      };
    } else {
      this.tabCounters.attachments++;
    }
    beep();
  }
}

/** This must be binded to the editor */
export function onAttachmentsTabSelected() {
  if (this.tabCounters.attachments == undefined) {
    this.tabCounters = {
      ...this.tabCounters,
      attachments: 0
    };
  } else {
    this.tabCounters.attachments = 0;
  }
}

export default {
  mixins: [EditorMixin],

  components: {
    Attachments,
    TagAlternatives
  },

  props: {
    /**
     * [
     *   {
     *     name: CUSTOMER,
     *     label: 'Allegati cliente',
     *     serverPath: 'customers',
     *     getId: (value) => value.customer.id
     *   },
     *   {
     *     name: REQUEST,
     *     label: 'Allegati richiesta',
     *     serverPath: 'credit-assignments/requests',
     *     getId: (value) => value.id
     *   },
     * ]
     */
    contextList: {
      type: Array,
      required: true
    },

    unsatisfiedTagAlternatives: {
      type: Array,
      default: undefined
    }
  },

  data() {
    return {
      counters: {},
      selectedContext:
        this.filteredContextList &&
        this.filteredContextList.length &&
        this.filteredContextList[0]
    };
  },

  computed: {
    filteredContextList() {
      const list = this.contextList.filter(
        c => c.getPermissions(this.value) && c.getPermissions(this.value).view
      );
      return list;
    }
  },

  mounted() {
    this.selectedContext =
      this.filteredContextList &&
      this.filteredContextList.length &&
      this.filteredContextList[0];
    this.subscribe();
  },

  beforeDestroy() {
    this.unsubscribe();
  },

  methods: {
    getOptions() {
      return this.filteredContextList.map(this.context2option);
    },

    context2option(context) {
      const contextId = context.serverPath + "/" + context.subcontext;
      const count = this.counters[contextId];
      const countStr = count ? " (" + count + ")" : "";

      const ret = {
        value: contextId,
        text: context.label + countStr
      };
      // console.log('context2option(', context, ') = ', ret);
      return ret;
    },

    context2id(context) {
      const ret = context.serverPath + "/" + context.subcontext;
      // console.log('context2id(', context, ') = ', ret);
      return ret;
    },

    id2context(id) {
      const ret = this.filteredContextList.find(
        context => id === context.serverPath + "/" + context.subcontext
      );
      // console.log('id2context(', id, ') = ', ret);
      return ret;
    },

    canUpload() {
      const permissions = this.selectedContext.getPermissions(this.value);
      //console.log('permissions', permissions);
      return permissions.upload;
    },

    // =========================================================================

    resolveContextServerPath(c) {
      return c.serverPath;
    },

    subscribe() {
      //console.log("Subscribing", this.context, this.contextId);

      for (let c of this.filteredContextList) {
        const serverPath = this.resolveContextServerPath(c);

        const topic =
          "/" +
          serverPath +
          "/cloud/" +
          c.getId(this.value) +
          "/" +
          c.subcontext;

        notifier.subscribe(
          topic,
          m => this.onEventReceived(serverPath, c.subcontext, m),
          s => this.storeSubscription(s)
        );

        notifier.subscribePrivate(
          m => this.onEventReceived(serverPath, c.subcontext, m),
          s => this.storeSubscription(s),
          topic
        );
      }
    },

    storeSubscription(s) {
      if (!this.subscriptions) {
        this.subscriptions = [];
      }
      this.subscriptions.push(s);
    },

    unsubscribe() {
      if (this.subscriptions) {
        console.log("Unsubscribing", this.subscriptions);
        for (let s of this.subscriptions) {
          s.unsubscribe();
        }
        this.subscriptions = undefined;
      }
    },

    onEventReceived(context, subcontext, data) {
      console.log("Event received (ws) - ", context, subcontext, ":", data);
      if (
        this.$refs.attachments.context === context &&
        this.$refs.attachments.subcontext === subcontext
      ) {
        if (data.type === "UPLOAD") {
          this.$refs.attachments.addAttachment(data.attachment);
        } else if (data.type === "UPDATE") {
          this.$refs.attachments.replaceAttachment(data.attachment);
        } else if (data.type === "DELETE") {
          this.$refs.attachments.removeAttachment(data.attachment);
        } else {
          throw Error("Unsupported event type: " + data.type);
        }
        //console.log('Add attachment');
        //this.$refs.messages.addMessage(data);
      } else {
        if (data.type === "UPLOAD") {
          const contextId = context + "/" + subcontext;
          // console.log('Updating counter for ', contextId);
          if (this.counters[contextId] !== undefined) {
            this.counters[contextId]++;
          } else {
            const counters = { ...this.counters };
            counters[contextId] = 1;
            this.counters = counters;
          }
        }
      }
      //this.messages.push(data);
      this.$emit("attachment-event", { context, subcontext, ...data });
      //beep();
    },

    selectContext(ctx) {
      const contextId = this.context2id(ctx);
      if (this.counters[contextId] !== undefined) {
        this.counters[contextId] = 0;
      } else {
        const counters = { ...this.counters };
        counters[contextId] = 0;
        this.counters = counters;
      }
      this.selectedContext = ctx;
    }
  }
};
</script>
